import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import { useLeave } from "../../contexts/api";
import { useHRData } from "../../contexts/app/hrData";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import { usePermissions, Permission } from "../../contexts/app/permissions";
import type { LeaveType } from "../../api/leave/Leave.typed";
import { COLOR_PALETTE_LEAVE } from "../../utils/colorPalette";
import LeaveTypesHeader from "./display/LeaveTypesHeader";
import LeaveTypesTable from "./display/LeaveTypesTable";
import { colorTokens, ctaAccentColor } from "../../styles/globalStyles";
import DarkTypography from "../common/DarkTypography";
import { NO_ACCESS_MESSAGE } from "../common/DashboardPage";
import { isPlatformLeaveType } from "../../utils/leaveHelpers";

export default function LeaveTypesPage() {
  const { createLeaveType, updateLeaveType, deleteLeaveType } = useLeave();
  const { leaveTypes: types, loading, reload } = useHRData();
  const modal = useConfirmationModal();
  const { hasPermission } = usePermissions();
  const canAccessTypes =
    hasPermission(Permission.LEAVE_ACCESS) &&
    (hasPermission(Permission.LEAVE_TYPES_VIEW) || hasPermission(Permission.LEAVE_TYPES_EDIT));
  const canEdit = hasPermission(Permission.LEAVE_TYPES_EDIT);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [draft, setDraft] = React.useState<Partial<LeaveType>>({});

  const startEdit = React.useCallback((lt?: LeaveType) => {
    if (lt != null && isPlatformLeaveType(lt)) {
      return;
    }
    if (lt) {
      setEditingId(lt.ID);
      setDraft({ ...lt });
    } else {
      setEditingId(0);
      setDraft({
        Name: "",
        IsActive: true,
        RequiresValidation: true,
        UsesCounter: false,
        Unit: "days",
        DefaultMonthlyAccrual: 0,
        AllowsNegative: false,
        Description: "",
        Color: ctaAccentColor,
      });
    }
  }, []);

  const cancelEdit = React.useCallback(() => {
    setEditingId(null);
    setDraft({});
  }, []);

  const save = React.useCallback(async () => {
    if (editingId === null) return;
    const name = String(draft.Name ?? "").trim();
    if (!name) return;
    const ok = await modal.showConfirmation(
      editingId === 0 ? "Créer le type de congé ?" : "Enregistrer les modifications ?",
      editingId === 0 ? "Un nouveau type sera créé." : "Les changements seront sauvegardés.",
    );
    if (!ok) return;
    const payload: Record<string, unknown> = {
      Name: name,
      IsActive: Boolean(draft.IsActive),
      RequiresValidation: Boolean(draft.RequiresValidation),
      UsesCounter: Boolean(draft.UsesCounter),
      Unit: draft.Unit === "hours" ? "hours" : "days",
      AllowsNegative: Boolean(draft.AllowsNegative),
      Color: String(draft.Color ?? colorTokens.text.muted),
    };
    const accrual = draft.DefaultMonthlyAccrual;
    if (accrual !== undefined && accrual !== null && !Number.isNaN(Number(accrual))) {
      payload.DefaultMonthlyAccrual = Number(accrual);
    }
    const desc = String(draft.Description ?? "").trim();
    if (desc) payload.Description = desc;
    if (editingId === 0) {
      await createLeaveType(payload as Omit<LeaveType, "ID">);
    } else {
      await updateLeaveType(editingId, payload as Omit<LeaveType, "ID">);
    }
    await reload();
    cancelEdit();
  }, [createLeaveType, draft, editingId, reload, modal, updateLeaveType, cancelEdit]);

  const remove = React.useCallback(
    async (id: number) => {
      const target = types.find((x) => x.ID === id);
      if (target != null && isPlatformLeaveType(target)) {
        return;
      }
      const ok = await modal.showConfirmation(
        "Supprimer le type de congé ?",
        "Cette action est irréversible.",
      );
      if (!ok) return;
      await deleteLeaveType(id);
      await reload();
    },
    [deleteLeaveType, reload, modal, types],
  );

  if (!canAccessTypes) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: "50vh",
          p: 3,
        }}
      >
        <DarkTypography
          level="body-lg"
          colorVariant="muted"
          sx={{ textAlign: "center", maxWidth: 400 }}
        >
          {NO_ACCESS_MESSAGE}
        </DarkTypography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      )}
      <LeaveTypesHeader onAdd={() => startEdit()} disabled={loading || !canEdit} />
      <LeaveTypesTable
        types={types}
        colorSet={COLOR_PALETTE_LEAVE}
        loading={loading}
        editingId={editingId}
        draft={draft}
        onDraftChange={setDraft}
        onStartEdit={startEdit}
        onCancelEdit={cancelEdit}
        onSave={save}
        onRemove={remove}
        canEdit={canEdit}
      />
    </Box>
  );
}
